import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, Trash2, AlertCircle, Check, Star } from 'lucide-react';
import { useThemeStore } from '../../store/theme';
import { useAuthStore } from '../../store/auth';
import { useWorkspaceStore, workspaceColors, type Workspace } from '../../store/workspace';

interface WorkspaceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspace: Workspace | null;
}

const colorOptions: { id: Workspace['color']; name: string }[] = [
  { id: 'pink', name: 'Pink' },
  { id: 'yellow', name: 'Yellow' },
  { id: 'purple', name: 'Purple' },
  { id: 'mint', name: 'Mint' },
  { id: 'blue', name: 'Blue' },
];

const API_URL = import.meta.env.VITE_API_URL || '/api';

export default function WorkspaceSettingsModal({
  isOpen,
  onClose,
  workspace,
}: WorkspaceSettingsModalProps) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const { token } = useAuthStore();
  const { updateWorkspace, deleteWorkspace, workspaces, setCurrentWorkspace } = useWorkspaceStore();

  const [name, setName] = useState('');
  const [color, setColor] = useState<Workspace['color']>('purple');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);

  // Reset form when workspace changes
  useEffect(() => {
    if (workspace) {
      setName(workspace.name);
      setColor(workspace.color);
      setError(null);
      setShowDeleteConfirm(false);
    }
  }, [workspace]);

  const handleSave = async () => {
    if (!workspace || !token) return;
    if (!name.trim()) {
      setError('Workspace name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/workspaces/${workspace.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: name.trim(), color }),
      });

      const data = await response.json() as { success: boolean; data?: Workspace; error?: string };

      if (data.success && data.data) {
        updateWorkspace(workspace.id, data.data);
        onClose();
      } else {
        setError(data.error || 'Failed to update workspace');
      }
    } catch {
      setError('Failed to update workspace');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!workspace || !token) return;

    setDeleting(true);
    setError(null);

    const success = await deleteWorkspace(token, workspace.id);
    if (success) {
      onClose();
    } else {
      setError('Failed to delete workspace');
    }
    setDeleting(false);
  };

  const handleSetDefault = async () => {
    if (!workspace || !token) return;

    setSettingDefault(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/workspaces/${workspace.id}/set-default`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json() as { success: boolean; data?: Workspace; error?: string };

      if (data.success && data.data) {
        // Update all workspaces to reflect the new default
        workspaces.forEach(w => {
          if (w.id === workspace.id) {
            updateWorkspace(w.id, { ...w, is_default: 1 });
          } else if (w.is_default === 1) {
            updateWorkspace(w.id, { ...w, is_default: 0 });
          }
        });
      } else {
        setError(data.error || 'Failed to set as default');
      }
    } catch {
      setError('Failed to set as default');
    }
    setSettingDefault(false);
  };

  if (!workspace) return null;

  const isDefault = workspace.is_default === 1;
  const isOnlyWorkspace = workspaces.length <= 1;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
          >
            <div
              className={`w-full max-w-md rounded-2xl shadow-2xl ${
                isDark ? 'bg-dark-800 border border-border' : 'bg-white border border-slate-200'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: workspaceColors[color] }}
                  >
                    <Settings className="w-5 h-5 text-white" />
                  </div>
                  <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Workspace Settings
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-500'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-500">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                {/* Workspace Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                    Workspace Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter workspace name"
                    className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                      isDark
                        ? 'bg-dark-700 border-border text-white placeholder-text-muted focus:border-accent-purple'
                        : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500/20`}
                  />
                </div>

                {/* Color Selection */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                    Color
                  </label>
                  <div className="flex gap-3">
                    {colorOptions.map((option) => {
                      const isSelected = color === option.id;
                      return (
                        <button
                          key={option.id}
                          onClick={() => setColor(option.id)}
                          className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform ${
                            isSelected ? 'scale-110 ring-2 ring-offset-2' : 'hover:scale-105'
                          }`}
                          style={{
                            backgroundColor: workspaceColors[option.id],
                            boxShadow: isSelected ? `0 0 0 2px ${isDark ? '#1a1a2e' : 'white'}, 0 0 0 4px ${workspaceColors[option.id]}` : 'none',
                          }}
                          title={option.name}
                        >
                          {isSelected && <Check className="w-5 h-5 text-white" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Default Workspace */}
                <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Star className={`w-5 h-5 ${isDefault ? 'text-yellow-500 fill-yellow-500' : isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Default Workspace
                        </p>
                        <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          {isDefault ? 'This is your default workspace' : 'Set as default workspace'}
                        </p>
                      </div>
                    </div>
                    {!isDefault && (
                      <button
                        onClick={handleSetDefault}
                        disabled={settingDefault}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          isDark
                            ? 'bg-dark-600 hover:bg-dark-500 text-white'
                            : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                        }`}
                      >
                        {settingDefault ? 'Setting...' : 'Set Default'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Danger Zone */}
                {!isOnlyWorkspace && (
                  <div className={`p-4 rounded-xl border ${isDark ? 'border-red-500/30 bg-red-500/5' : 'border-red-200 bg-red-50'}`}>
                    {!showDeleteConfirm ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Trash2 className="w-5 h-5 text-red-500" />
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              Delete Workspace
                            </p>
                            <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                              Permanently delete this workspace and all its data
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowDeleteConfirm(true)}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-500/10 hover:bg-red-500/20 text-red-500 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                          Are you sure you want to delete <strong>{workspace.name}</strong>? This action cannot be undone.
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowDeleteConfirm(false)}
                            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              isDark
                                ? 'bg-dark-600 hover:bg-dark-500 text-white'
                                : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                            }`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleDelete}
                            disabled={deleting}
                            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors"
                          >
                            {deleting ? 'Deleting...' : 'Yes, Delete'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className={`flex justify-end gap-3 p-6 border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <button
                  onClick={onClose}
                  className={`px-5 py-2.5 rounded-xl font-medium transition-colors ${
                    isDark
                      ? 'bg-dark-700 hover:bg-dark-600 text-white'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !name.trim()}
                  className={`px-5 py-2.5 rounded-xl font-medium transition-colors ${
                    saving || !name.trim()
                      ? 'bg-purple-500/50 cursor-not-allowed'
                      : 'bg-purple-500 hover:bg-purple-600'
                  } text-white`}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
